<script setup lang="ts">
/**
 * Generic chat message row: avatar | body (meta + content + extras + actions).
 * Used by Formica Chat (multi-user) and Agent (user/bot).
 *
 * `tone="system"` is a notice between the messages ("Anna joined"): its
 * content sits centred in the row. `attention` outlines the row a jump has
 * just landed on (a link, a search hit), `editing` marks the message being
 * edited in place — both are the caller's state.
 *
 * `variant="bubble"` frames the body as a chat bubble (hairline border,
 * `radius-lg`, up to 82 % of the width, one type step smaller than the row)
 * instead of a flat row. `mine` aligns the own message to the right in the
 * brand-subtle surface; the others stay left in the plain surface. The sender
 * line goes into `#meta` (flex row like the row's, in the brand-subtle text
 * colour). A bubble has no avatar column unless the `#avatar` slot renders
 * something (an empty slot or only comments does not count) — then the avatar
 * sits beside the bubble, left for the others and right for `mine`. The
 * row's hover tint is gone: `active` and the pointer on the bubble tint the
 * bubble, `attention` and `editing` colour it. `#actions` (only when the
 * slot renders something) sit in the flow beside the bubble, on the free side
 * (right of the others' bubbles, left of `mine`): invisible until the pointer,
 * the focus or `active` is on the message, so nothing shifts when they show,
 * and the bubble gives way to them instead of being overlapped.
 *
 * `variant="messenger"` is the messenger look: the bubble is a filled surface
 * without a border, the own message is right-aligned in the solid brand fill.
 * `#sender` sits above the bubble, `#avatar` beside its last line (its column
 * stays, empty, on every message of the others so a group lines up; the own
 * messages have none), `#time` floats at the end of the text, `#below` holds
 * what hangs under the bubble (reactions, the thread link) and `#actions` fade
 * in beside the bubble, on its free side, covering nothing and moving nothing.
 * `position` closes up the corners of a group.
 *
 * `mine` means nothing in the row variant; `tone` means nothing in the
 * bubble (`tone="system"` is a notice between rows and does not centre a
 * bubble).
 */
import { Comment, Fragment, Text, useSlots, type VNode } from 'vue';
import type { NxHue } from '../lib/hue';

withDefaults(
  defineProps<{
    /** Visual tone for default avatar when no #avatar slot. */
    tone?: 'user' | 'agent' | 'system' | 'neutral';
    /**
     * `row` is the flat message row; `bubble` frames the body as a chat bubble;
     * `messenger` is the filled bubble of a messenger.
     */
    variant?: 'row' | 'bubble' | 'messenger';
    /** Only with a bubble variant: the own message, right-aligned. */
    mine?: boolean;
    /** Only with `variant="messenger"`: where the message sits in a group of one sender. */
    position?: 'single' | 'first' | 'middle' | 'last';
    /** Only with `variant="messenger"`: the colour of the `#sender` line. */
    senderHue?: NxHue;
    /** Optional compact density (threads). */
    compact?: boolean;
    /** Highlight / context-menu open state. */
    active?: boolean;
    /**
     * A hint for the eye that the user was just taken to this message (a deep
     * link, a search hit): a brand outline and a subtle fill that fade in. The
     * caller sets it and takes it back after a moment; the row keeps no timer.
     * Not a selection, and no change for assistive technology.
     */
    attention?: boolean;
    /** The message is being edited in place: the row gets a warning background, hover included. */
    editing?: boolean;
  }>(),
  {
    tone: 'neutral',
    variant: 'row',
    mine: false,
    position: 'single',
    senderHue: undefined,
    compact: false,
    active: false,
    attention: false,
    editing: false,
  }
);

defineEmits<{
  contextmenu: [e: MouseEvent];
}>();

const slots = useSlots();

/**
 * Whether a slot renders anything — the same check as `rendersContent` in
 * NxFeedItem: a caller that passes its slot on to every message hands over
 * one that renders nothing (an empty fragment, a `v-if` comment).
 */
function rendersContent(nodes: unknown): boolean {
  if (!Array.isArray(nodes)) return false;
  return nodes.some((node) => {
    const vnode = node as VNode;
    if (vnode.type === Comment) return false;
    if (vnode.type === Fragment) return rendersContent(vnode.children);
    if (vnode.type === Text) return String(vnode.children ?? '').trim() !== '';
    return true;
  });
}

/** The row shows its actions for any actions slot; a bubble only for one that renders something. */
function showActions(variant: 'row' | 'bubble' | 'messenger'): boolean {
  return variant === 'row' ? !!slots.actions : rendersContent(slots.actions?.());
}

/** The row keeps its avatar column (empty or not); a bubble only has one for a real avatar. */
function showAvatar(variant: 'row' | 'bubble' | 'messenger', tone: string): boolean {
  if (variant === 'row') return !!slots.avatar || tone !== 'system';
  return rendersContent(slots.avatar?.());
}
</script>

<template>
  <article
    v-if="variant === 'messenger'"
    class="nx-chat-msg-m"
    :class="[
      `is-${position}`,
      { 'is-mine': mine, active, 'is-attention': attention, 'is-editing': editing },
    ]"
    data-testid="nx-chat-message"
    @contextmenu="$emit('contextmenu', $event)"
  >
    <div v-if="!mine" class="nx-chat-msg-m__avatar">
      <slot name="avatar" />
    </div>
    <div class="nx-chat-msg-m__body">
      <div
        v-if="$slots.sender"
        class="nx-chat-msg-m__sender"
        :style="senderHue ? { '--chat-msg-sender': `var(--nxt-hue-${senderHue}-fg)` } : undefined"
      >
        <slot name="sender" />
      </div>
      <div class="nx-chat-msg-m__bubble">
        <div class="nx-chat-msg__content">
          <slot />
          <span v-if="$slots.time && !$slots.extras" class="nx-chat-msg-m__time">
            <slot name="time" />
          </span>
        </div>
        <div v-if="$slots.extras" class="nx-chat-msg-m__extras">
          <slot name="extras" />
        </div>
        <div v-if="$slots.time && $slots.extras" class="nx-chat-msg-m__time-row">
          <span class="nx-chat-msg-m__time">
            <slot name="time" />
          </span>
        </div>
      </div>
      <div v-if="$slots.below" class="nx-chat-msg-m__below">
        <slot name="below" />
      </div>
      <div v-if="showActions(variant)" class="nx-chat-msg-m__actions">
        <slot name="actions" />
      </div>
    </div>
  </article>
  <article
    v-else
    class="nx-chat-msg"
    :class="[
      `tone-${variant === 'bubble' ? 'neutral' : tone}`,
      {
        'variant-bubble': variant === 'bubble',
        compact,
        active,
        'is-attention': attention,
        'is-editing': editing,
        'is-mine': variant === 'bubble' && mine,
      },
    ]"
    data-testid="nx-chat-message"
    @contextmenu="$emit('contextmenu', $event)"
  >
    <div v-if="showAvatar(variant, tone)" class="nx-chat-msg__avatar">
      <slot name="avatar" />
    </div>
    <div class="nx-chat-msg__body">
      <div v-if="$slots.meta" class="nx-chat-msg__meta">
        <slot name="meta" />
      </div>
      <div class="nx-chat-msg__content">
        <slot />
      </div>
      <div v-if="$slots.extras" class="nx-chat-msg__extras">
        <slot name="extras" />
      </div>
    </div>
    <div v-if="showActions(variant)" class="nx-chat-msg__actions">
      <slot name="actions" />
    </div>
  </article>
</template>

<style scoped>
.nx-chat-msg {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: var(--nxt-space-sm);
  padding: var(--nxt-space-xs) calc(var(--nxt-space-md) * 1.125);
  position: relative;
  /* No outline at rest: the colour the attention outline fades in from. */
  outline-color: transparent;
  transition: background-color var(--nxt-motion-fast) ease;
  box-sizing: border-box;
}
.nx-chat-msg.compact {
  padding: calc(var(--nxt-space-xs) * 0.75) calc(var(--nxt-space-md) * 0.875);
  gap: calc(var(--nxt-space-xs) * 1.25);
}
.nx-chat-msg:hover,
.nx-chat-msg.active {
  background: color-mix(in srgb, var(--nxt-brand-bg) 5%, transparent);
}
.nx-chat-msg.tone-system {
  justify-content: center;
  padding: calc(var(--nxt-space-xs) * 0.75) calc(var(--nxt-space-md) * 1.125);
  background: transparent;
}
.nx-chat-msg.tone-system:hover {
  background: transparent;
}
/* A notice is centred as a whole: the body shrinks to its content instead of
   filling the row, and its lines centre. */
.nx-chat-msg.tone-system .nx-chat-msg__body {
  flex: 0 1 auto;
  text-align: center;
}
.nx-chat-msg.tone-system .nx-chat-msg__content {
  text-align: center;
}
/* Editing: the row stays in the warning colour while the pointer is on it. */
.nx-chat-msg.is-editing,
.nx-chat-msg.is-editing:hover {
  background: var(--nxt-warning-subtle-bg);
}
/* Attention: the hint that a jump just landed on this message. An outline, so
   nothing moves, with a subtle fill; both fade in from transparent and the
   caller takes them back after a moment. After hover, so the hint stays. */
.nx-chat-msg.is-attention,
.nx-chat-msg.is-attention:hover {
  outline: calc(var(--nxt-hairline) * 2) solid var(--nxt-brand-border);
  outline-offset: calc(var(--nxt-hairline) * -2);
  background: var(--nxt-brand-subtle-bg);
  transition:
    background-color var(--nxt-motion-fast) ease,
    outline-color var(--nxt-motion-slow);
}

/* Bubble: the frame is the body, so the row itself stays bare — no padding, no
   hover tint, no outline. Own messages flip the row, which right-aligns the
   bubble and puts its avatar on the right. */
.nx-chat-msg.variant-bubble,
.nx-chat-msg.variant-bubble.compact {
  padding: 0;
}
.nx-chat-msg.variant-bubble.is-mine {
  flex-direction: row-reverse;
}
.nx-chat-msg.variant-bubble:hover,
.nx-chat-msg.variant-bubble.active,
.nx-chat-msg.variant-bubble.is-editing,
.nx-chat-msg.variant-bubble.is-editing:hover,
.nx-chat-msg.variant-bubble.is-attention,
.nx-chat-msg.variant-bubble.is-attention:hover {
  background: transparent;
  outline: none;
}
.nx-chat-msg.variant-bubble .nx-chat-msg__body {
  flex: 0 1 auto;
  max-width: 82%;
  border: var(--nxt-hairline) solid var(--nxt-border);
  border-radius: var(--nxt-radius-lg);
  background: var(--nxt-surface-bg);
  padding: var(--nxt-space-xs) var(--nxt-space-md);
  outline-color: transparent;
  transition:
    background-color var(--nxt-motion-fast) ease,
    outline-color var(--nxt-motion-slow);
}
/* The pointer and `active` lay the row's brand tint over the bubble: an image,
   so it works on the plain, the own, the editing and the attention surface. */
.nx-chat-msg.variant-bubble .nx-chat-msg__body:hover,
.nx-chat-msg.variant-bubble.active .nx-chat-msg__body {
  background-image: linear-gradient(
    color-mix(in srgb, var(--nxt-brand-bg) 8%, transparent),
    color-mix(in srgb, var(--nxt-brand-bg) 8%, transparent)
  );
}
.nx-chat-msg.variant-bubble.is-mine .nx-chat-msg__body {
  border-color: var(--nxt-brand-border);
  background-color: var(--nxt-brand-subtle-bg);
}
.nx-chat-msg.variant-bubble.is-editing .nx-chat-msg__body {
  background-color: var(--nxt-warning-subtle-bg);
}
.nx-chat-msg.variant-bubble.is-attention .nx-chat-msg__body {
  outline: calc(var(--nxt-hairline) * 2) solid var(--nxt-brand-border);
  outline-offset: calc(var(--nxt-hairline) * -2);
  background-color: var(--nxt-brand-subtle-bg);
}
/* Typography only: the meta row keeps the row's flex and gap, so a name and a
   time stay apart. */
.nx-chat-msg.variant-bubble .nx-chat-msg__meta {
  margin-bottom: var(--nxt-space-2xs);
  font-size: calc(var(--nxt-font-size-xs) * 0.85);
  font-weight: var(--nxt-font-weight-bold);
  letter-spacing: normal;
  color: var(--nxt-brand-subtle-fg);
}
.nx-chat-msg.variant-bubble .nx-chat-msg__content {
  font-size: var(--nxt-font-size-xs);
  letter-spacing: normal;
}
/* Actions are a flex item beside the bubble, on its free side (the own
   message's row is reversed, so they land on the left). They are only hidden,
   not removed: their room is kept, so showing them shifts nothing, and the
   bubble shrinks for them (its body has min-width 0) instead of being overlapped.
   The row's own reveal rules apply; a focus inside shows them too. */
.nx-chat-msg.variant-bubble .nx-chat-msg__actions {
  position: static;
  flex: none;
  margin-top: calc(var(--nxt-space-xs) * 0.75);
}
.nx-chat-msg.variant-bubble:focus-within .nx-chat-msg__actions {
  opacity: 1;
  pointer-events: auto;
}

.nx-chat-msg__avatar {
  flex: 0 0 auto;
  margin-top: calc(
    var(--nxt-size-unit) * 0.0625
  ); /* stylelint-disable-line declaration-property-unit-disallowed-list -- 1px optical nudge */
}

.nx-chat-msg__body {
  flex: 1 1 auto;
  min-width: 0;
  text-align: left;
}

.nx-chat-msg__meta {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: calc(var(--nxt-space-xs) * 0.875);
  margin-bottom: calc(var(--nxt-space-2xs) / 2);
  font-size: var(--nxt-font-size-sm);
  font-weight: var(--nxt-font-weight-semibold);
  letter-spacing: -0.01em;
  color: var(--nxt-fg);
}

.nx-chat-msg__content {
  font-size: var(--nxt-font-size-sm);
  line-height: var(--nxt-line-height-normal);
  color: var(--nxt-fg);
  word-break: break-word;
  letter-spacing: -0.005em;
  text-align: left;
}

/* Shared markdown chrome for apps that inject HTML */
.nx-chat-msg__content :deep(p) {
  margin: 0 0 var(--nxt-space-xs);
}
.nx-chat-msg__content :deep(p:last-child) {
  margin-bottom: 0;
}
.nx-chat-msg__content :deep(pre) {
  background: var(--nxt-fg);
  color: var(--nxt-surface-bg);
  padding: var(--nxt-space-sm) var(--nxt-space-md);
  border-radius: var(--nxt-radius-md);
  overflow-x: auto;
  font-size: var(--nxt-font-size-sm);
  margin: calc(var(--nxt-space-xs) * 0.75) 0;
}
.nx-chat-msg__content :deep(code) {
  background: var(--nxt-surface-sunken-bg);
  padding: calc(var(--nxt-space-2xs) / 2) calc(var(--nxt-space-2xs) * 1.25);
  border-radius: var(--nxt-radius-sm);
  font-size: 0.85em;
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
}
.nx-chat-msg__content :deep(pre code) {
  background: transparent;
  padding: 0;
  color: inherit;
}
.nx-chat-msg__content :deep(ul),
.nx-chat-msg__content :deep(ol) {
  margin: var(--nxt-space-2xs) 0 var(--nxt-space-xs);
  padding-left: calc(var(--nxt-space-md) * 1.375);
}
.nx-chat-msg__content :deep(a) {
  color: var(--nxt-brand-bg);
}

.nx-chat-msg__extras {
  margin-top: calc(var(--nxt-space-xs) * 0.75);
}

.nx-chat-msg__actions {
  position: absolute;
  top: calc(var(--nxt-space-xs) * 0.75);
  right: var(--nxt-space-sm);
  opacity: 0;
  pointer-events: none;
  transition: opacity var(--nxt-motion-fast) ease;
}
.nx-chat-msg:hover .nx-chat-msg__actions,
.nx-chat-msg.active .nx-chat-msg__actions {
  opacity: 1;
  pointer-events: auto;
}

/* Messenger: the row is bare, the bubble is the surface. Corners close up towards
   the neighbours of a group; the own messages mirror them. */
.nx-chat-msg-m {
  --chat-msg-corner: 1.1em;
  --chat-msg-joint: 0.35em;
  display: flex;
  align-items: flex-end;
  gap: var(--nxt-space-xs);
  padding: var(--nxt-hairline) var(--nxt-space-md);
  box-sizing: border-box;
}
.nx-chat-msg-m.is-single,
.nx-chat-msg-m.is-first {
  margin-top: var(--nxt-space-sm);
}
.nx-chat-msg-m.is-mine {
  justify-content: flex-end;
}
.nx-chat-msg-m__avatar {
  flex: none;
  width: var(--nxt-avatar-size-md);
  display: flex;
  justify-content: center;
}
.nx-chat-msg-m__body {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: calc(var(--nxt-space-2xs) / 2);
  min-width: 0;
  /* Leaves the free side room for the actions, down to half a narrow row. */
  max-width: max(50%, min(74%, calc(100% - var(--nxt-size-unit) * 15)));
}
.nx-chat-msg-m.is-mine .nx-chat-msg-m__body {
  align-items: flex-end;
}
.nx-chat-msg-m__sender {
  display: flex;
  align-items: baseline;
  gap: var(--nxt-space-xs);
  padding-inline-start: var(--nxt-space-sm);
  font-size: var(--nxt-font-size-xs);
  font-weight: var(--nxt-font-weight-medium);
}
.nx-chat-msg-m__sender :deep(button) {
  color: var(--chat-msg-sender, var(--nxt-fg-muted));
}
.nx-chat-msg-m__bubble {
  min-width: 0;
  max-width: 100%;
  padding: 0.5em 0.85em;
  background: var(--nxt-surface-raised-bg);
  border-radius: var(--chat-msg-corner);
  box-shadow: var(--nxt-shadow-sm);
  transition: background-color var(--nxt-motion-fast) ease;
}
.nx-chat-msg-m.is-first .nx-chat-msg-m__bubble {
  border-end-start-radius: var(--chat-msg-joint);
}
.nx-chat-msg-m.is-middle .nx-chat-msg-m__bubble {
  border-start-start-radius: var(--chat-msg-joint);
  border-end-start-radius: var(--chat-msg-joint);
}
.nx-chat-msg-m.is-last .nx-chat-msg-m__bubble {
  border-start-start-radius: var(--chat-msg-joint);
}
.nx-chat-msg-m.is-mine .nx-chat-msg-m__bubble {
  border-radius: var(--chat-msg-corner);
  background: var(--nxt-brand-bg);
  color: var(--nxt-brand-fg);
}
.nx-chat-msg-m.is-mine.is-first .nx-chat-msg-m__bubble {
  border-end-end-radius: var(--chat-msg-joint);
}
.nx-chat-msg-m.is-mine.is-middle .nx-chat-msg-m__bubble {
  border-start-end-radius: var(--chat-msg-joint);
  border-end-end-radius: var(--chat-msg-joint);
}
.nx-chat-msg-m.is-mine.is-last .nx-chat-msg-m__bubble {
  border-start-end-radius: var(--chat-msg-joint);
}
/* The time sits at the end of the last line when it fits, else on its own. */
.nx-chat-msg-m .nx-chat-msg__content {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  justify-content: flex-end;
  column-gap: 0.9em;
  color: inherit;
}
.nx-chat-msg-m .nx-chat-msg__content > :deep(*) {
  min-width: 0;
}
.nx-chat-msg-m .nx-chat-msg__content :deep(.nx-markdown p) {
  font-size: inherit;
  font-weight: var(--nxt-font-weight-regular);
}
.nx-chat-msg-m.is-mine .nx-chat-msg__content :deep(.nx-markdown) {
  color: inherit;
}
.nx-chat-msg-m.is-mine .nx-chat-msg__content :deep(a) {
  color: inherit;
  text-decoration: underline;
}
.nx-chat-msg-m__time {
  flex: none;
  margin-inline-start: auto;
  display: inline-flex;
  align-items: center;
  gap: 0.25em;
  padding-block-end: 0.2em;
  font-size: calc(var(--nxt-font-size-xs) * 0.88);
  line-height: 1;
  color: var(--nxt-fg-subtle);
}
.nx-chat-msg-m.is-mine .nx-chat-msg-m__time {
  color: color-mix(in srgb, var(--nxt-brand-fg) 75%, transparent);
}
.nx-chat-msg-m__extras {
  margin-top: var(--nxt-space-xs);
}
.nx-chat-msg-m__time-row {
  display: flex;
  justify-content: flex-end;
  margin-top: var(--nxt-space-2xs);
}
.nx-chat-msg-m__below {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--nxt-space-2xs);
  padding-inline: var(--nxt-space-sm);
}
.nx-chat-msg-m__actions {
  position: absolute;
  bottom: 0;
  inset-inline-start: calc(100% + var(--nxt-space-2xs));
  z-index: var(--nxt-z-above);
  opacity: 0;
  pointer-events: none;
  transition: opacity var(--nxt-motion-fast) var(--nxt-ease-out);
}
.nx-chat-msg-m.is-mine .nx-chat-msg-m__actions {
  inset-inline: auto calc(100% + var(--nxt-space-2xs));
}
/* The short delay lets the pointer pass over messages without anything showing. */
.nx-chat-msg-m:hover .nx-chat-msg-m__actions,
.nx-chat-msg-m:focus-within .nx-chat-msg-m__actions,
.nx-chat-msg-m.active .nx-chat-msg-m__actions {
  opacity: 1;
  pointer-events: auto;
  transition-delay: var(--nxt-motion-fast);
}
.nx-chat-msg-m.is-editing .nx-chat-msg-m__bubble {
  background: var(--nxt-warning-subtle-bg);
  color: var(--nxt-fg);
}
.nx-chat-msg-m.is-attention .nx-chat-msg-m__bubble {
  outline: calc(var(--nxt-hairline) * 2) solid var(--nxt-brand-border);
  outline-offset: var(--nxt-hairline);
}

@media (prefers-reduced-motion: reduce) {
  .nx-chat-msg-m__bubble,
  .nx-chat-msg-m__actions,
  .nx-chat-msg,
  .nx-chat-msg.is-attention,
  .nx-chat-msg.variant-bubble .nx-chat-msg__body,
  .nx-chat-msg__actions {
    transition: none;
  }
}
</style>
